import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const Stat = ({ label, value, color = '#00d55b' }) => (
  <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
    <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color }}>{value ?? '—'}</div>
  </div>
);

const COLORS = ['#00d55b', '#185FA5', '#f59e0b', '#ff4444', '#7c3aed'];

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get('/api/chats/analytics').then(r => setData(r.data.data)).catch(() => {});
  }, []);

  const dailyData = data?.daily_chats?.map(d => ({ date: new Date(d.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }), chats: d.count })) || [];

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Analytics</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>Track your chat performance and visitor insights</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 28 }}>
          <Stat label="Total chats" value={data?.total_chats} />
          <Stat label="Today" value={data?.today_chats} />
          <Stat label="Resolved" value={data?.resolved_chats} />
          <Stat label="Missed" value={data?.missed_chats} color={data?.missed_chats > 0 ? '#ff4444' : '#555'} />
          <Stat label="Avg rating" value={data?.avg_rating ? `${data.avg_rating}★` : '—'} color="#f59e0b" />
          <Stat label="Chats used" value={data ? `${data.chats_used_this_month}/${data.max_chats_per_month ?? '∞'}` : '—'} color="#fff" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* Daily chats chart */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 20 }}>Daily chats (last 30 days)</h3>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={dailyData}>
                  <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 12 }} />
                  <Line type="monotone" dataKey="chats" stroke="#00d55b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: 13 }}>No data yet</div>}
          </div>

          {/* Agent performance */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 20 }}>Agent performance</h3>
            {data?.agent_performance?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.agent_performance}>
                  <XAxis dataKey="name" tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#555', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 12 }} />
                  <Bar dataKey="chats_handled" fill="#00d55b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: 13 }}>No agent data yet</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Top countries */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 20 }}>Top countries</h3>
            {data?.top_countries?.length > 0 ? (
              <div style={{ display: 'flex', gap: 20 }}>
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie data={data.top_countries} dataKey="count" nameKey="country" cx="50%" cy="50%" outerRadius={60}>
                      {data.top_countries.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                  {data.top_countries.map((c, i) => (
                    <div key={c.country} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: '#888', flex: 1 }}>{c.country}</span>
                      <span style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: 13 }}>No data yet</div>}
          </div>

          {/* Usage this month */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 20 }}>Usage this month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#888' }}>Chats used</span>
                  <span style={{ fontSize: 12, color: '#fff' }}>{data?.chats_used_this_month ?? 0} / {data?.max_chats_per_month ?? '∞'}</span>
                </div>
                {data?.max_chats_per_month && (
                  <div style={{ height: 8, background: '#1a1a1a', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#00d55b', borderRadius: 4, width: `${Math.min(100, (data.chats_used_this_month / data.max_chats_per_month) * 100)}%`, transition: 'width 0.5s' }} />
                  </div>
                )}
              </div>
              {data?.agent_performance?.map(a => (
                <div key={a.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#888' }}>{a.name}</span>
                    <span style={{ fontSize: 12, color: '#fff' }}>{a.chats_handled} chats</span>
                  </div>
                  <div style={{ height: 6, background: '#1a1a1a', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#185FA5', borderRadius: 3, width: `${Math.min(100, (a.chats_handled / (data?.total_chats || 1)) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashLayout>
  );
}